/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 * LmsLaunchContext.js
 * LmsLaunchContext for LMS launch
 */

import * as constants from '../constants';
import CommonUtils from './CommonUtils';
import PathUtils from './PathUtils';

/**
 * Saves LMS launch info (contextId + isLMS) in user-scoped localStorage
 * so grade sync and Console interop work after redirects and new-tab PMC returns.
 */
export default class LmsLaunchContext {
  static getLocalStorageKey() {
    const userId = CommonUtils.getCurrentUserId();

    return `${constants.LMS_LAUNCH_CONTEXT}_${userId}`;
  }

  static loadSavedLaunch() {
    const path = window.location.pathname;

    if (!path.startsWith('/home2/')) {
      return null;
    }

    const mlmcontextencoded = path.split('/')[2];

    if (mlmcontextencoded) {
      const mlmBookData = CommonUtils.getMLMRouteBookData({ params: { mlmcontextencoded } }) || {};
      const isValidMLMRoute = mlmBookData?.bookId || mlmBookData?.courseId;

      if (!isValidMLMRoute) {
        return null;
      }
    }

    return CommonUtils.getLocalStorge(LmsLaunchContext.getLocalStorageKey(), true);
  }

  static saveLaunch(launch) {
    CommonUtils.setLocalStorge(LmsLaunchContext.getLocalStorageKey(), launch, true);
  }

  static getContextIdFromUrl() {
    return PathUtils.getQueryParameterbyName(constants.CONTEXT_ID) || '';
  }

  static hasLmsInUrl() {
    return CommonUtils.isLMSCourse();
  }

  /**
   * Saves LMS launch info from the URL when isLMS=Y and contextId are present.
   * Overwrites the saved launch when a new contextId arrives.
   */
  static saveFromUrl() {
    const contextId = LmsLaunchContext.getContextIdFromUrl();
    const launchedFromLms = LmsLaunchContext.hasLmsInUrl();

    if (!launchedFromLms || !contextId) {
      return;
    }

    const savedLaunch = LmsLaunchContext.loadSavedLaunch();

    if (savedLaunch?.contextId === contextId) {
      return;
    }

    LmsLaunchContext.saveLaunch({
      contextId,
      isLms: true
    });
  }

  /**
   * Returns the LMS contextId from the URL, or the saved value if the URL no longer has it.
   *
   * @returns {string}
   */
  static getContextId() {
    LmsLaunchContext.saveFromUrl();

    const contextIdFromUrl = LmsLaunchContext.getContextIdFromUrl();

    if (contextIdFromUrl) {
      return contextIdFromUrl;
    }

    const { contextId } = LmsLaunchContext.loadSavedLaunch() || {};

    return contextId || '';
  }

  /**
   * Returns true when the user launched from an LMS (URL param or saved launch).
   *
   * @returns {boolean}
   */
  static isLmsLaunch() {
    LmsLaunchContext.saveFromUrl();

    if (LmsLaunchContext.hasLmsInUrl()) {
      return true;
    }

    return LmsLaunchContext.loadSavedLaunch()?.isLms === true;
  }
}
